import React, { useEffect, useRef, useState } from 'react';

import { ReactComponent as EyeIcon } from '../../assets/icons/eye-icon.svg';
import { ReactComponent as EyeSlashIcon } from '../../assets/icons/eye-slash-icon.svg';

type InputType = 'text' | 'number' | 'email' | 'password';

interface CustomInputProperties {
  inputLabel?: string;
  type: InputType;
  placeholder: string;
  value: string | number | null;
  name: string;
  onChangeValue: (name: string, value: string | number) => void;
  valueRequired?: boolean;
  readOnly?: boolean;
  svgIcon?: JSX.Element;
  frondIcon?: JSX.Element;
  svgIconClickFunc?: () => void;
  error?: boolean;
  errorMessage?: string;
  autoFocus?: boolean;
  fontSize?: 'small' | 'medium' | 'large';
  hint?: boolean;
  hintMessage?: string;
  disabled?: boolean;
  disallowNegative?: boolean;
}

/**
 * Custom input box
 */
function CustomInput({
  inputLabel,
  type,
  name,
  placeholder,
  value,
  onChangeValue,
  valueRequired,
  readOnly,
  svgIcon,
  frondIcon,
  autoFocus,
  svgIconClickFunc,
  error,
  errorMessage,
  fontSize = 'large',
  hint,
  hintMessage,
  disabled,
  disallowNegative,
}: CustomInputProperties) {
  const inputReference = useRef<HTMLInputElement>(null);
  const [inputType, setInputType] = useState<InputType>(type);
  /**
   * Handle input change
   */
  const handleValueChange = (changeValue: string | number) => {
    let finalValue = changeValue;
    if (type === 'number' && disallowNegative) {
      if (typeof finalValue === 'string') {
        const numericValue = Number(finalValue);
        if (numericValue < 0) {
          finalValue = finalValue.replace('-', '');
        }
      } else if (typeof finalValue === 'number' && finalValue < 0) {
        finalValue = 0;
      }
    }
    onChangeValue(name, finalValue);
  };

  /**
   * Input's start and end white space trim helper
   * @param event
   */
  const internalHandleBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    const enteredValue = event.target.value;
    let trimmed = enteredValue.trim();
    if (type === 'number' && disallowNegative) {
      const numericValue = Number(trimmed);
      if (numericValue < 0) {
        trimmed = '0';
      }
    }
    if (trimmed !== String(value)) {
      handleValueChange(trimmed);
    }
  };
  useEffect(() => {
    if (autoFocus && inputReference.current) {
      inputReference.current.focus(); // Focus the input field if condition is true
    }
  }, [autoFocus]);

  return (
    <div className={`custom-input-wrap ${fontSize}`}>
      {inputLabel && (
        <p className={`input-label ${error && 'error'}`}>
          {inputLabel}
          {valueRequired && <span>*</span>}
        </p>
      )}
      <div className={`custom-input-container ${error && 'error'}`}>
        {frondIcon && <span>{frondIcon}</span>}
        <input
          type={type === 'password' ? inputType : type}
          placeholder={placeholder}
          value={value !== null ? value : undefined}
          onChange={(event) => handleValueChange(event.target.value)}
          onBlur={internalHandleBlur}
          required={valueRequired}
          readOnly={readOnly}
          ref={inputReference}
          disabled={disabled}
          min={disallowNegative && type === 'number' ? 0 : undefined}
          onWheel={(event) => event.currentTarget.blur()}
        />
        {svgIcon && type !== 'password' && (
          <span role="presentation" onClick={svgIconClickFunc}>
            {svgIcon}
          </span>
        )}
        {(() => {
          let passwordIcon: JSX.Element | null = null; // Initialize the type as either a JSX element or null
          if (type === 'password' && inputType === 'password') {
            passwordIcon = (
              <span role="presentation" onClick={() => setInputType('text')}>
                <EyeIcon />
              </span>
            );
          } else if (type === 'password' && inputType === 'text') {
            passwordIcon = (
              <span role="presentation" onClick={() => setInputType('password')}>
                <EyeSlashIcon />
              </span>
            );
          }
          return passwordIcon; // Return the icon or null
        })()}
      </div>
      {error && <p className="custom-input-error-message">{errorMessage}</p>}
      {hint && <p className="custom-input-hint-message">{hintMessage}</p>}
    </div>
  );
}

CustomInput.defaultProps = {
  inputLabel: '',
  valueRequired: false,
  readOnly: false,
  svgIcon: null,
  frondIcon: null,
  fontSize: 'large',
  svgIconClickFunc: null,
  error: false,
  errorMessage: '',
  autoFocus: false,
  hint: true,
  hintMessage: '',
  disabled: false,
  disallowNegative: false,
};

export default CustomInput;
